// TIP-614 — the brief price, as the backend last reported it.
// WHY A SLICE AND NOT CONSTANTS
// The checkout used to compute its line items from literals in
// counterpartyBriefPricing.js (14900 / 13410 fils, max quantity 20). Every one
// of those was wrong for at least one environment — one already sells at
// AED 10 with a max of 100 — and nothing would have caught the drift: the
// backend test reads Django settings, the frontend test asserted its own
// literal, so both stayed green while the two disagreed.
// So there is now ONE source, the `pricing` block on
// GET /api/briefs/allowance/, and this slice holds it.
// HOW IT STAYS FRESH
// Filled by a matcher on the allowance query's own fulfilled action, not by a
// dispatch at a call site. That matters: the allowance is refetched whenever
// it is invalidated — after an order is placed, after a purchase is credited,
// on a revisit to the dashboard — and every one of those refreshes the price
// here for free. A manual dispatch would have to be remembered in each of
// those places, and would eventually be forgotten in one.
// SCOPED TO A COMPANY
// The member price differs by tier, and tier is per company. The companyID the
// figures arrived for is stored alongside them so a company switch cannot show
// the previous company's prices: selectBriefPricing returns null on a
// mismatch rather than something plausible and wrong.
import { createSlice, isFulfilled } from "@reduxjs/toolkit";

const initialState = {
  // The block exactly as the endpoint sent it, wire names and all, so the
  // pricing helpers read one shape whether it came from here or a prop.
  pricing: null,
  // Which company these figures describe.
  companyID: null,
  // When they arrived. Not rendered; it is here so a stale-price question can
  // be answered from the store rather than guessed at.
  receivedAt: null,
};

/* Is this the allowance query coming back? */
const isAllowanceFulfilled = (action) =>
  isFulfilled(action) && action.meta?.arg?.endpointName === "getBriefsAllowance";

const briefPricingSlice = createSlice({
  name: "briefPricing",
  initialState,
  reducers: {
    /** Only for tests and the ?cbState= review path, which has no backend. */
    setBriefPricing: (state, action) => {
      state.pricing = action.payload?.pricing ?? null;
      state.companyID = action.payload?.companyID ?? null;
      state.receivedAt = Date.now();
    },
    clearBriefPricing: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(isAllowanceFulfilled, (state, action) => {
      const pricing = action.payload?.pricing ?? null;
      // A 404 (the unclaimed gate) resolves with no pricing at all. Keeping
      // the previous company's block would be worse than holding none.
      state.pricing = pricing;
      state.companyID = action.meta?.arg?.originalArgs?.companyID ?? null;
      state.receivedAt = Date.now();
    });

    // The price belongs to a signed-in company. Dropped with the session, the
    // same way the active company and the query cache are.
    builder.addMatcher(
      (action) =>
        action.type === "auth/logout" ||
        action.type === "auth/clearUserInfo" ||
        action.type === "auth/clearIdentityScopedState",
      () => initialState
    );
  },
});

export const { setBriefPricing, clearBriefPricing } = briefPricingSlice.actions;

/* The pricing block for `companyID`, or null. */
export const selectBriefPricing = (state, companyID) => {
  const slice = state?.briefPricing;
  if (!slice?.pricing) return null;
  if (companyID && slice.companyID && slice.companyID !== companyID) return null;
  return slice.pricing;
};

export const selectBriefPricingCompany = (state) => state?.briefPricing?.companyID ?? null;

export default briefPricingSlice.reducer;
